import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { withImage, useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import AccessibleDescription from '../accessible-description/accessible-description';
import Link from '../link/link';
import Box from '../box/box';
import Heading from '../heading/heading';
import Text from '../text/text';
import Image from '../image/image';
import LayoutContext from '../context/layout-context';

export interface PopularListProps {
  /** Allows component to receive additional styles */
  className?: string;
  /** The posts to be displayed. */
  popular: Lucid.Directus.Generated.Blog_Post[];
}

const PopularList: FC<PropsWithChildren<PopularListProps>> = ({ popular, className }) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_blog_choose_category', 'mktg_blog_read_more', 'mktg_blog_popular_now'],
    shortCode,
  });
  const popularList = popular.filter(p => p.translations?.length).slice(0, 4);

  return popularList?.length ? (
    <>
      <AccessibleDescription isForScreenReaderOnly as="h2" SRText={withSmartling('mktg_blog_popular_now')} />
      <ul
        css={theme => ({
          label: 'blogPopularList',
          display: 'flex',
          flexDirection: 'column',
          rowGap: theme.space.s24,
          columnGap: theme.space.s80,
          alignItems: 'stretch',
          listStyle: 'none',
          margin: '0 auto',
          maxWidth: '580px',
          [theme.breakpoints.bp768.min]: {
            rowGap: theme.space.s48,
          },
          [theme.breakpoints.bp1220.min]: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridTemplateRows: 'repeat(3, auto)',
            maxWidth: '100%',
          },
        })}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
      >
        {popularList.map((post, i) => {
          if (!post.translations?.[0]) {
            return null;
          }
          const { image, categories } = post;
          const { id, heading, path, summary } = post.translations[0];
          const mainCategory = categories?.[0]?.blog_category_id;
          const categoryTranslatedData = mainCategory?.translations?.[0];

          return (
            <li
              key={`popular-list-id-${id}-${i}`}
              css={theme => [
                {
                  alignItems: 'center',
                  gridColumnGap: theme.space.s24,
                },
                i === 0 && {
                  gridRowStart: '1',
                  gridRowEnd: 'span 3',
                  paddingBottom: theme.space.s64,
                  [theme.breakpoints.bp768.min]: {
                    paddingBottom: 0,
                  },
                },
                i !== 0 && {
                  paddingTop: theme.space.s40,
                  borderTop: '1px solid',
                  borderColor: theme.colors.white,
                  [theme.breakpoints.bp768.min]: {
                    paddingTop: 0,
                    borderTop: 'none',
                  },
                },
              ]}
            >
              <Box
                css={theme => [
                  {
                    position: 'relative',
                  },
                  i !== 0 && {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    [theme.breakpoints.bp768.min]: {
                      alignItems: 'center',
                      display: 'grid',
                      gridTemplateColumns: '180px 1fr',
                    },
                  },
                ]}
              >
                {image && image.filename_disk ? (
                  <Box>
                    <Image
                      css={theme => [i !== 0 && { maxWidth: '180px' }, i === 0 && { marginBottom: theme.space.s64 }]}
                      imageAlt={image.translations?.[0]?.alt ?? ''}
                      imageSrc={withImage(image)}
                      loading="lazy"
                    />
                  </Box>
                ) : (
                  <Box></Box>
                )}
                <Box
                  css={theme => [
                    i !== 0 && {
                      display: 'flex',
                      flexDirection: 'column',
                      flexBasis: '100%',
                      marginTop: theme.space.s32,
                      paddingBottom: theme.space.s24,
                      [theme.breakpoints.bp768.min]: {
                        paddingLeft: theme.space.s24,
                        marginLeft: theme.space.s24,
                        marginTop: '0',
                        borderLeft: '1px solid',
                        borderColor: theme.colors.coolGrey[10],
                      },
                    },
                  ]}
                >
                  {categoryTranslatedData?.name && (
                    <Link
                      aria-label={`${withSmartling('mktg_blog_choose_category')} - ${categoryTranslatedData?.name}`}
                      css={theme => ({ zIndex: theme.zIndices.z10, position: 'relative' })}
                      href={categoryTranslatedData.path?.url}
                      variant="secondaryAlternate"
                    >
                      {categoryTranslatedData?.name}
                    </Link>
                  )}
                  <Box css={{ display: 'block', color: 'inherit' }}>
                    <Heading
                      as="h3"
                      css={theme => [
                        {
                          color: 'inherit',
                          marginTop: theme.space.s24,
                          marginBottom: theme.space.s16,
                        },
                        i === 0 && { marginBottom: theme.space.s40 },
                      ]}
                      variant={i === 0 ? 'heading2XL' : 'headingL'}
                    >
                      {heading}
                    </Heading>
                  </Box>
                  <Text
                    css={[
                      {
                        marginTop: '0',
                        color: 'inherit',
                      },
                      i,
                    ]}
                  >
                    <Text
                      as="span"
                      css={[
                        i !== 0 && {
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        },
                      ]}
                      variant="paragraphM400"
                    >
                      {summary}
                    </Text>
                    <Link
                      css={theme => ({
                        display: 'block',
                        marginTop: theme.space.s40,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          right: 0,
                          left: 0,
                        },
                      })}
                      href={path?.url}
                      iconAnimation="navigational"
                      variant="cardOutlineLink"
                    >
                      {withSmartling('mktg_read_more') || 'Read more'}
                    </Link>
                  </Text>
                </Box>
              </Box>
            </li>
          );
        })}
      </ul>
    </>
  ) : null;
};

export default PopularList;
